import { Injectable } from '@angular/core';
import { CustomXmlPart } from '../interfaces/customxmlpart';
import { AbstractPartService } from '../utils/abstract-part.service';
import { Dom, IAttribute } from '../utils/dom';
import { SortOption } from '../utils/fieldconfig';
import { FieldContext } from '../utils/fieldcontext';
import { ThEachAttribute, ThEachAttributeParser } from '../utils/th-attributes/theach/theach-attribute';
import { ThTextAttribute, ThTextAttributeFactory } from '../utils/th-attributes/thtext/thtext-attribute';
import { ThTextFieldPathExtractor } from '../utils/th-attributes/thtext/thtext-fieldpath-extractor';
import { XPath } from '../utils/xpath-utils';
import { DataSourcePathService } from './data-source-path.service';
import { LoggingService } from './logging.service';
import { SortPathService } from './sort-path.service';
import { WordService } from './word.service';

@Injectable({ providedIn: 'root' })
export class ModelPartService extends AbstractPartService {
  private static readonly KP_SRC_ATTRIBUTE_NAME = 'kp:src';

  constructor(
    private wordService: WordService,
    private dataSourcePathService: DataSourcePathService,
    private loggingService: LoggingService,
    private sortPathService: SortPathService
  ) {
    super();
  }

  // technically doesn't need to be async as we're just returning the Promise
  create(namespace: string): Promise<CustomXmlPart> {
    const nowUtc = new Date().toUTCString();
    const skeletonModelXml = `<${namespace} xmlns:th="http://www.thymeleaf.org" xmlns:kp="https://kyloepartners.com"><kpInfo>${nowUtc}</kpInfo></${namespace}>`;
    return this.wordService.createCustomXmlPart(skeletonModelXml);
  }

  async add(
    part: CustomXmlPart,
    ancestorXpath: XPath | null,
    parentXpath: XPath | null,
    fieldContext: FieldContext,
    refId: number
  ): Promise<XPath> {
    this.loggingService.debug('ModelPartService', 'add', `ModelPart.add(${ancestorXpath}, ${parentXpath}, ${fieldContext.field.path})`);

    const dom = await this.getDOM(part);
    this.ensureEntityElement(dom, fieldContext.entity.entity);

    const entityXpath = `/${fieldContext.namespace}/${fieldContext.entity.entity}`;
    parentXpath = parentXpath ?? entityXpath;
    const parentElement = this.getElement(dom, parentXpath);
    if (!parentElement) {
      throw new Error(`Failed to find parent element '${parentXpath}'`);
    }

    const ancestorElement = this.getElement(dom, ancestorXpath);
    const leafElementName = this.buildLeafElementName(fieldContext.leaf, refId);
    const dataSrcPath = this.getDataSourcePath(ancestorElement, fieldContext);
    console.debug('ModelPartService', 'add', `leafElementName: ${leafElementName}, dataSrcPath: ${dataSrcPath}`);

    // TODO: BW - change to build attributes instead of addXXXX?
    if (fieldContext.isContainer) {
      this.addContainer(dom, parentElement, leafElementName, dataSrcPath);
    } else if (fieldContext.isIterable) {
      this.addIterable(dom, parentElement, leafElementName, dataSrcPath, fieldContext);
    } else {
      this.addField(dom, parentElement, leafElementName, dataSrcPath, fieldContext);
    }

    await part.setXml(dom.outerHTML);

    return `${parentXpath}/${leafElementName}`;
  }

  async update(part: CustomXmlPart, existingXPath: XPath, fieldContext: FieldContext): Promise<void> {
    this.loggingService.debug('ModelPartService', 'update', `ModelPart.update(${existingXPath},${fieldContext.field.path})`);

    const dom = await this.getDOM(part);
    const existingElement = this.getElement(dom, existingXPath);
    if (!existingElement) {
      throw new Error(`Failed to find existing element '${existingXPath}'`);
    }

    if (fieldContext.isIterable) {
      this.updateIterable(existingElement, fieldContext);
    } else {
      this.updateField(existingElement, fieldContext);
    }

    await part.setXml(dom.outerHTML);
  }

  async removePath(xpath: string | null, part: CustomXmlPart): Promise<void> {
    this.loggingService.debug('ModelPartService', 'removePath', `${xpath}`);
    if (!xpath) {
      return;
    }

    const modelDom = await this.getDOM(part);
    const element = modelDom.tryGetChildFromPath(xpath);
    if (element) {
      element.remove();
      await part.setXml(modelDom.outerHTML);
    }
  }

  async getTargetEntity(part: CustomXmlPart): Promise<string | undefined> {
    const dom = await this.getDOM(part);
    const found = dom.documentElement.firstChild;
    return found ? found.nodeName : undefined;
  }

  private ensureEntityElement(dom: Dom, entity: string): void {
    // e.g. /invenias/assignmentsListItem or /invenias/currentUser
    const found = Dom.findChildByTagName(dom.documentElement, entity);
    if (!found) {
      const entityElem = dom.createElement(entity);
      dom.documentElement.appendChild(entityElem);
    }
  }

  private buildLeafElementName(proposedName: string, refId: number): string {
    // remove [ and ] for example when list status iterator values such as [index] are used
    const leafElementName = proposedName.replaceAll(/[\[\]]/g, '');
    return `${leafElementName}_${refId}`;
  }

  private getDataSourcePath(ancestorElement: Element | null, fieldContext: FieldContext): string {
    if (!ancestorElement) {
      // placing a field at the 'top/root' of the docx
      if (this.doesNotRequireAncestorElement(fieldContext)) {
        return this.dataSourcePathService.getFullyQualifiedPath(fieldContext);
      } // use the field path as the source

      this.loggingService.error('ModelPartService', 'Error', `Expected an ancestor path for field '${fieldContext.field.path}'`);
      throw new Error(`You cannot insert '${fieldContext.field.label}' at the current location in the document.`);
    }

    // only do this for containers (might need to check not th:text as well)
    if (!ancestorElement.hasAttribute(ThEachAttribute.QUALIFIED_NAME)) {
      const kpSrc = ancestorElement.getAttribute(ModelPartService.KP_SRC_ATTRIBUTE_NAME);
      if (kpSrc) {
        return this.dataSourcePathService.getOwnerPath(kpSrc, fieldContext);
      }
    }

    // if it's not an "iterable" element (to-many) then the parser will throw an error - indicates we haven't thought about something probably
    const thEachAttribVariableName = ThEachAttributeParser.getVariableName(ancestorElement);

    return this.dataSourcePathService.getOwnerPath(thEachAttribVariableName, fieldContext); // use the ancestor variable as the source
  }

  private doesNotRequireAncestorElement(fieldContext: FieldContext): boolean {
    return (
      fieldContext.isEntityField ||
      fieldContext.isScriptField ||
      fieldContext.isImageField ||
      fieldContext.isConditionalField ||
      fieldContext.isAiAssistField
    );
  }

  private addIterable(dom: Dom, parent: Element, leafElementName: string, dataSrcPath: string, fieldContext: FieldContext): void {
    const element = this.createIterableElement(dom, leafElementName, dataSrcPath, fieldContext);
    parent.appendChild(element);
  }

  private updateIterable(existingElement: Element, fieldContext: FieldContext): void {
    const variableName = ThEachAttributeParser.getVariableName(existingElement);

    const dataSrcPath = existingElement.getAttribute(ModelPartService.KP_SRC_ATTRIBUTE_NAME);
    if (!dataSrcPath) {
      throw new Error(`Missing kp:src attribute on th:each element.`);
    }

    const thEach: ThEachAttribute = new ThEachAttribute(variableName, dataSrcPath);

    thEach.conditions = fieldContext.conditions;

    if (fieldContext.sortOption) {
      thEach.sort = this.handleSortOption(fieldContext, fieldContext.sortOption);
    } else {
      thEach.sort = undefined;
    }

    if (fieldContext.limit !== undefined) {
      // explicit check in case limit can be 0
      thEach.limit = fieldContext.limit;
    } else {
      thEach.limit = undefined;
    }

    existingElement.setAttribute(ThEachAttribute.QUALIFIED_NAME, thEach.value);
  }

  private createIterableElement(dom: Dom, leafElementName: string, dataSrcPath: string, fieldContext: FieldContext): Element {
    const attributes: IAttribute[] = [];

    const variableName = this.makeVariableName(leafElementName);
    const thEachAttrib = new ThEachAttribute(variableName, dataSrcPath);
    thEachAttrib.conditions = fieldContext.conditions;
    if (fieldContext.sortOption) {
      thEachAttrib.sort = this.handleSortOption(fieldContext, fieldContext.sortOption);
    }
    if (fieldContext.limit !== undefined) {
      // explicit check in case limit can be 0
      thEachAttrib.limit = fieldContext.limit;
    }

    attributes.push(thEachAttrib);

    const kpSrcAttribute = {
      qualifiedName: ModelPartService.KP_SRC_ATTRIBUTE_NAME,
      value: dataSrcPath,
    };
    attributes.push(kpSrcAttribute);

    return dom.createElement(leafElementName, attributes);
  }

  private handleSortOption(
    fieldContext: FieldContext,
    sortOption: SortOption
  ): { fieldPath: string; direction: number } | undefined {
    const fieldPath =
      this.sortPathService.getTemplateSortField(fieldContext, sortOption) ?? this.getLeaf(sortOption.fieldPath);
    if (!fieldPath) {
      return undefined;
    }
    return { fieldPath, direction: sortOption.dirn };
  }

  private addField(dom: Dom, parent: Element, leafElementName: string, dataSrcPath: string, fieldContext: FieldContext): void {
    const newElement = this.createFieldElement(dom, leafElementName, dataSrcPath, fieldContext);
    parent.appendChild(newElement);
  }

  private updateField(existingElement: Element, fieldContext: FieldContext): void {
    // the system-default field options should guarantee that formatting options are populated

    let dataSrcPath = existingElement.getAttribute(ModelPartService.KP_SRC_ATTRIBUTE_NAME);
    if (!dataSrcPath) {
      // this is for legacy templates where the kp:src attribute did not exist
      dataSrcPath = ThTextFieldPathExtractor.fromElement(existingElement);
      console.debug('No kp:src found on th:text. Adding kp:src attribute during update.', dataSrcPath);
      existingElement.setAttribute(ModelPartService.KP_SRC_ATTRIBUTE_NAME, dataSrcPath);
    }

    if (fieldContext.isConditionalField) {
      // the datasource can change for a conditional field (i.e. user can select a different 'condition' field)
      // be nice to call this.getDatasourcePath(..)
      dataSrcPath = this.dataSourcePathService.getFullyQualifiedPath(fieldContext);
      existingElement.setAttribute(ModelPartService.KP_SRC_ATTRIBUTE_NAME, dataSrcPath);
    }

    const thTextAttrib = ThTextAttributeFactory.make(dataSrcPath, fieldContext);
    existingElement.setAttribute(ThTextAttribute.QUALIFIED_NAME, thTextAttrib.value);
  }

  private createFieldElement(dom: Dom, leafElementName: string, dataSrcPath: string, fieldContext: FieldContext): Element {
    const thTextAttrib = ThTextAttributeFactory.make(dataSrcPath, fieldContext);

    const kpSrcAttribute = {
      qualifiedName: ModelPartService.KP_SRC_ATTRIBUTE_NAME,
      value: dataSrcPath,
    };

    const element = dom.createElement(leafElementName, [thTextAttrib, kpSrcAttribute]);
    element.textContent = this.createSampleText([fieldContext.entity.label, fieldContext.field.label]);
    return element;
  }

  private addContainer(dom: Dom, parent: Element, leafElementName: string, dataSrcPath: string): void {
    const newElement = dom.createElement(leafElementName, [
      {
        qualifiedName: ModelPartService.KP_SRC_ATTRIBUTE_NAME,
        value: dataSrcPath,
      },
    ]);
    parent.appendChild(newElement);
  }

  private makeVariableName(name: string): string {
    return `${name}_item`;
  }

  private getElement(dom: Dom, xpath: XPath | null): Element | null {
    if (!xpath) {
      return null;
    }
    return dom.getChildFromPath(xpath);
  }

  private createSampleText(parts: string[]): string {
    return `|${parts.join('-')}|`;
  }

  private getLeaf(path: string): string {
    return path.split('.')?.slice(-1)[0];
  }
}
